import { Injectable } from '@angular/core';
import {
  holdsAnyProfile,
  ListMembersResponse,
  ListUserMembershipsResponse,
  membershipStatus,
  OperablePoint,
  RemoveMemberResponse,
  SERVICE_POINT_PROFILES,
  ServicePointOption,
  ServicePointProfile,
  UpsertMemberInput,
  UpsertMemberResponse,
} from '../models/service-point.model';
import { SupabaseService } from './base/supabase.service';

interface ValidMembership {
  service_point_id: string;
  profiles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ServicePointService {
  private membershipsInFlight = new Map<string, Promise<ValidMembership[]>>();

  constructor(private sbs: SupabaseService) {}

  async hasAnyValidProfile(userId: string, required: readonly ServicePointProfile[]): Promise<boolean> {
    try {
      const memberships = await this.validMemberships(userId);
      return memberships.some((membership) => holdsAnyProfile(membership.profiles, required));
    } catch (error) {
      console.error('[SERVICE POINT] Profile lookup failed', error);
      return false;
    }
  }

  async listManageablePoints(role: string | null | undefined, userId: string): Promise<ServicePointOption[]> {
    if (role === 'ADMINISTRATOR') return this.queryActivePoints();
    if (role !== 'AGENT') return [];

    const memberships = await this.validMemberships(userId);
    const supervisedIds = memberships
      .filter((membership) => membership.profiles.includes('SUPERVISOR'))
      .map((membership) => membership.service_point_id);
    if (!supervisedIds.length) return [];
    return this.queryActivePoints(supervisedIds);
  }

  async listOperablePoints(
    role: string | null | undefined,
    userId: string,
    required: readonly ServicePointProfile[]
  ): Promise<OperablePoint[]> {
    if (role === 'ADMINISTRATOR') {
      const points = await this.queryActivePoints();
      return points.map((point) => ({ ...point, profiles: [...SERVICE_POINT_PROFILES] }));
    }
    if (role !== 'AGENT') return [];

    const memberships = (await this.validMemberships(userId)).filter((membership) =>
      holdsAnyProfile(membership.profiles, required)
    );
    if (!memberships.length) return [];

    const points = await this.queryActivePoints(memberships.map((membership) => membership.service_point_id));
    return points.map((point) => {
      const held = memberships.find((membership) => membership.service_point_id === point.id)?.profiles ?? [];
      return { ...point, profiles: SERVICE_POINT_PROFILES.filter((profile) => held.includes(profile)) };
    });
  }

  listMembers(servicePointId: string, search?: string): Promise<ListMembersResponse> {
    const body: Record<string, unknown> = { service_point_id: servicePointId };
    if (search) body['search'] = search;
    return this.sbs.callLambda('service-point-members-list', 'POST', body);
  }

  // ADMINISTRATOR only: one agent's memberships across points (agent "Tools" tab, CPK-5087).
  listMembershipsByUser(userId: string): Promise<ListUserMembershipsResponse> {
    return this.sbs.callLambda('service-point-members-list', 'POST', { user_id: userId });
  }

  upsertMember(input: UpsertMemberInput): Promise<UpsertMemberResponse> {
    return this.sbs.callLambda('service-point-member-upsert', 'POST', input);
  }

  removeMember(servicePointId: string, userId: string): Promise<RemoveMemberResponse> {
    return this.sbs.callLambda('service-point-member-remove', 'POST', {
      service_point_id: servicePointId,
      user_id: userId,
    });
  }

  private validMemberships(userId: string): Promise<ValidMembership[]> {
    const pending = this.membershipsInFlight.get(userId);
    if (pending) return pending;

    const request = this.queryValidMemberships(userId).finally(() => this.membershipsInFlight.delete(userId));
    this.membershipsInFlight.set(userId, request);
    return request;
  }

  private async queryValidMemberships(userId: string): Promise<ValidMembership[]> {
    const { data, error } = await this.sbs.supabase
      .from('cp_service_point_member')
      .select('service_point_id, profiles, valid_from, valid_until')
      .eq('user_id', userId);
    if (error) throw error;

    const now = Date.now();
    return (data ?? []).filter((membership) => membershipStatus(membership, now) === 'ACTIVE');
  }

  private async queryActivePoints(ids?: string[]): Promise<ServicePointOption[]> {
    let query = this.sbs.supabase.from('cp_service_point').select('id, name').eq('active', true).order('name');
    if (ids) query = query.in('id', ids);

    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  }
}
